import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type ErrorState = 'warning' | 'error';

@Component({
  selector: 'lib-text-field-alls',
  templateUrl: './text-fields.component.html',
  styleUrls: ['./text-fields.component.scss'],
  imports: [CommonModule, ReactiveFormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TextFieldsComponent implements OnInit, OnChanges, OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() alignmentRight = true;
  @Input() errorMessage = '';
  @Input() fieldLabel = '';
  @Input() fieldName = '';
  @Input() placeholder = '';
  @Input() formControlData: any = '';
  @Input() rowData: any = {};
  @Input() unitsOfMeasure = '';
  @Input() showUOM = false;
  @Input() required = false;
  @Input() roundOffRequired = false;
  @Input() fieldLength = 10;
  @Input() onlyNumeric = false;
  @Input() fullWidth = false;
  @Input() isSubmitted = false;
  @Input() isZeroAccepted = true;
  @Input() isReadOnly = false;
  @Input() showInUpperCase = false;
  @Input() showError = false;
  @Input() pageName = '';
  @Output() inputFieldData: EventEmitter<string> = new EventEmitter();
  public errorState: ErrorState;
  public errorMessageText = '';
  public textFieldForm: FormControl;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formControlData'] && this.textFieldForm) {
      const newValue = this.formControlData?.value?.[this.fieldName] ?? this.formControlData?.[this.fieldName] ?? null;
      this.textFieldForm.setValue(newValue, { emitEvent: false });
    }
    if (changes['rowData'] && this.textFieldForm && !this.formControlData) {
      this.textFieldForm.setValue(this.rowData ?? null, { emitEvent: false });
    }
    if (
      (changes['isSubmitted'] &&
        changes['isSubmitted'].currentValue === true) ||
      changes['errorMessage']
    ) {
      this.updateStatus();
    }
    if (
      (changes['showError'] || this.showError) &&
      (this.fieldName === 'storageTierFromDays' ||
        this.fieldName === 'storageTierToDays')
    ) {
      this.errorState = null;
      if (
        (changes['showError'] && changes['showError'].currentValue === true) ||
        this.showError
      ) {
        this.showErrorWarning();
      }
    }
  }

  ngOnInit(): void {
    if (this.formControlData) {
      if (this.required) {
        if (this.formControlData.value) {
          this.textFieldForm = new FormControl(
            this.formControlData.value[this.fieldName],
            {
              validators: [Validators.required],
            }
          );
        } else {
          if (
            this.formControlData[this.fieldName] === 0 &&
            this.roundOffRequired
          ) {
            this.textFieldForm = new FormControl('0.00', {
              validators: [Validators.required],
            });
          } else {
            this.textFieldForm = new FormControl(
              this.formControlData[this.fieldName],
              {
                validators: [Validators.required],
              }
            );
          }
        }
      } else {
        this.textFieldForm = new FormControl(
          this.formControlData.value[this.fieldName]
        );
      }
    } else {
      this.textFieldForm = new FormControl(this.rowData);
    }

    this.textFieldForm.statusChanges.subscribe(() => this.updateStatus());

    if (this.formControlData && typeof this.formControlData.valueChanges !== 'undefined') {
      const isFormGroup = this.formControlData.controls !== undefined;
      this.formControlData.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((val: any) => {
          const newValue = isFormGroup ? (val?.[this.fieldName] ?? null) : (val ?? null);
          if (this.textFieldForm.value !== newValue) {
            this.textFieldForm.setValue(newValue, { emitEvent: false });
          }
        });
    }
  }

  updateStatus() {
    if (
      this.textFieldForm &&
      (this.textFieldForm.value === '' || this.textFieldForm.value === null) &&
      (this.required ||
        (this.formControlData &&
          this.formControlData.value &&
          this.formControlData.value.isDataNotAvailable))
    ) {
      this.errorMessageText = this.errorMessage;
      this.showErrorWarning();
    } else if (this.textFieldForm &&
      !this.isZeroAccepted &&
      Number(this.textFieldForm.value) === 0
    ) {
      this.errorMessageText = 'Can not be 0';
      this.showErrorWarning();
    } else {
      this.errorMessageText = '';
      this.errorState = null;
    } // if (this.formControlData.value.fuelSurchargeType && this.formControlData.value.fuelSurchargeType.length > 0 && this.formControlData.value.fuelSurchargeType[0].description !== 'FSC included in Rate' && (this.textFieldForm.value && !this.isGreaterThanZero())) {
    //   this.errorMessageText = 'FSC rate can not be 0.0000.';
    //   this.showErrorWarning();
    // }
  }

  isGreaterThanZero() {
    return this.textFieldForm.value > 0 ? true : false;
  }

  showErrorWarning() {
    this.errorState = 'warning';
    if (this.isSubmitted) {
      this.errorState = 'error';
    }
  }

  /** it is used to update parent component */
  updateComponent() {
    this.updateStatus();
    if (this.roundOffRequired && this.textFieldForm.value) {
      const inputFieldValue = parseFloat(this.textFieldForm.value);
      if (!isNaN(inputFieldValue)) {
         const roundOffValue = this.pageName === 'storage' && this.fieldName === 'quantity' ? 2 : 4;
        this.textFieldForm.setValue(inputFieldValue.toFixed(roundOffValue), {
          emitEvent: false,
        });
      }
    }
    this.inputFieldData.emit(this.textFieldForm.value);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Allow only number */
  formatValue(): void {
    if (this.showInUpperCase) {
      const currentValue = this.textFieldForm.value || '';
      this.textFieldForm.setValue(currentValue.toUpperCase(), {
        emitEvent: false,
      });
    }

    if (this.onlyNumeric && this.roundOffRequired) {
      let input = this.textFieldForm.value || '';

      /** If input already has a decimal, do nothing */
      if (input.includes('.')) {
        return;
      }

      /**Remove non-numeric characters */
      input = input.replace(/\D/g, '');
      const cutOffValue = this.pageName === 'storage' && this.fieldName === 'quantity' ? 3 : 5;
      /** Auto-format only if length exceeds threshold */
      if (input.length > this.fieldLength - cutOffValue) {
        const formattedValue = `${input.slice(
          0,
          this.fieldLength - cutOffValue
        )}.${input.slice(this.fieldLength - cutOffValue, this.fieldLength)}`;
        this.textFieldForm.setValue(formattedValue, { emitEvent: false });
      }
    }
  }

  /** Allow only number */
  preventNonNumeric(event: KeyboardEvent): void {
    if (!this.onlyNumeric || this.isReadOnly) return;

    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'End',
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
    ];
    if (allowedKeys.includes(event.key)) return;
    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value || '';
    const selectionStart = inputElement.selectionStart ?? currentValue.length;
    const selectionEnd = inputElement.selectionEnd ?? currentValue.length;
    /** Allow only one dot */
    if (
      event.key === '.' &&
      (currentValue.includes('.') || !this.roundOffRequired)
    ) {
      event.preventDefault();
      return;
    }
    /** Allow only digits and one dot */
    if (
      !(
        (event.key >= '0' && event.key <= '9') ||
        (event.key === '.' && this.roundOffRequired)
      )
    ) {
      event.preventDefault();
      return;
    }
    /** Restrict to 4 digits after decimal */
    if (currentValue.includes('.')) {
      const [integerPart, decimalPart] = currentValue.split('.');
      const cursorInDecimal = selectionStart > integerPart.length;
      if (cursorInDecimal && decimalPart) {
        const newDecimalLength =
          decimalPart.length - (selectionEnd - selectionStart) + 1;
        if (newDecimalLength > 4) {
          event.preventDefault();
        }
      }
    }
  }

  clearIfZeroLike(): void {
    const val = this.textFieldForm?.value;
    const s = (val ?? '').toString().trim();
    /** Cases to clear: '0 not meaningful) -- Cases to clear: '0.000', '0.00', '0.0', '0', or numeric zero with any decimals */
    const isZeroishString = s.length > 0 && /^0+(\.0+)?$/.test(s);
    const isNumericZero = s.length > 0 && !isNaN(Number(s)) && Number(s) === 0;
    /**  If you want to clear only exactly '0.000', replace with: const shouldClear = s === '0.000'; */
    const shouldClear = isZeroishString || isNumericZero;
    if (shouldClear) {
      /** Clear without firing statusChanges to avoid flicker */
      this.textFieldForm.setValue('', { emitEvent: false });
      /** Also reset error presentation since it's now empty */
      this.errorMessageText = '';
      this.errorState = null;
    }
  }

  resetField() {
    this.textFieldForm.setValue('', { emitEvent: false });
    this.errorMessageText = '';
    this.errorState = null;
  }
}
